import React from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { authLogin, disconnect } from "requests/Auth";
import { getDentistaContato } from "requests/DentistaContato";
import { getTimeReset } from "requests/Config";
import { getMaterials } from "requests/Material";
import { notifications as getNotifications, readyNotification } from "requests/Notifications";
import { getOrthodonticElastics } from "requests/OrthodonticElastic";
import { getProcedures } from "requests/Procedure";
import { getReferrals } from "requests/Referrals";
import { getSchedule } from "requests/Schedule";
import { setAuthenticatedUser } from "shared/hocs";
import {
  IAuth,
  IAuthLogin,
  IAuthenticated,
  ILabelValue,
  ILabelValueProcedure,
} from "shared/types/Auth";
import { ITypes, IWire } from "shared/types/Material";
import { ISchedule, ScheduleLight, TimeSlot } from "shared/types/Schedule";
import { useWebSocket, ScheduleUpdatedEvent, PatientArrivedEvent, DentistNotificationEvent } from "shared/hooks/useWebSocket";
import { WEBSOCKET_CONFIG } from "shared/config/websocket";
import { INotification } from "shared/types/Notification";
import ContactUpdateModal from "components/ContactUpdateModal";
import { formatDateLabelFromYMD, getCurrentLocalDateYMD } from "shared/utils/date";

interface IProps {
  children: any;
}

export type IPageHome = "DEFAULT" | "INDICATORS" | "CONTROL" | "PATIENTS";

export interface IAuthContext {
  signIn: (formData: IAuthLogin, callback?: () => void) => void;
  loading: boolean;
  freeTime: boolean;
  setFreeTime: (value: boolean) => void;
  deslogged: (id: string) => void;
  searchPatients: string;
  setSearchPatients: (value: string) => void;
  schedules: (value: ISchedule) => void;
  user: IAuth;
  scheduleData: TimeSlot[];
  complementary: ILabelValue[];
  type: ITypes[];
  wire: IWire[];
  proceduresData: ILabelValueProcedure[];
  loadingSchedule: boolean;
  materials: (value: { userId: string; idPac: number }) => void;
  procedures: (value: { userId: string; type: number; idPac: number }) => void;
  referralsData: ILabelValue[];
  referrals: (value: { id: string; idSchedule: number; idPac: number }) => void;

  alerts: any[];
  setAlerts: (value: any[]) => void;
  modalOpen: boolean;
  setModalOpen: (value: boolean) => void;
  responses: { id: string; description: string; message: string }[];
  setResponses: (
    value: { id: string; description: string; message: string }[]
  ) => void;

  timeChoosen: string;
  setTimeChoosen: (value: string) => void;

  timeSelect: string;
  setTimeSelect: (value: string) => void;

  showServed: boolean;
  setShowServed: (value: boolean) => void;

  controlArrived: "SCHEDULE" | "ARRIVED";
  setControlArrived: (value: "SCHEDULE" | "ARRIVED") => void;

  timeRefresh: number;

  proceduresDataWithFlow: ILabelValueProcedure[];

  screensHome: IPageHome;
  setScreensHome: (value: IPageHome) => void;

  openCloseAniversario: boolean;
  setOpenCloseAniversario: (value: boolean) => void;

  wireValues: ILabelValue[];
  typeValues: ILabelValue[];
  orthodonticElasticValues: ILabelValue[];
  loadOrthodonticElastics: (userId: string) => void;

  // WebSocket
  isWebSocketConnected: boolean;

  // Notificações do dentista (via webhook)
  notifications: INotification[];
  markNotificationRead: (id: number) => void;

  // Atualização cadastral
  showContactUpdate: boolean;
}

const AuthContext = React.createContext({} as IAuthContext);

const AuthProvider: React.FC<IProps> = ({ children }) => {
  const navigate = useNavigate();

  const [loading, setLoading] = React.useState<boolean>(false);
  const [freeTime, setFreeTime] = React.useState<boolean>(false);
  const [showServed, setShowServed] = React.useState<boolean>(false);
  const [searchPatients, setSearchPatients] = React.useState<string>("");
  //const [user, setUser] = React.useState<IAuth>({} as IAuth);
  const [scheduleData, setScheduleData] = React.useState<TimeSlot[]>([]);
  const [complementary, setComplementary] = React.useState<ILabelValue[]>([]);
  const [proceduresData, setProceduresData] = React.useState<
    ILabelValueProcedure[]
  >([]);

  const [proceduresDataWithFlow, setProceduresDataWithFlow] = React.useState<
    ILabelValueProcedure[]
  >([]);
  const [referralsData, setReferralsData] = React.useState<ILabelValue[]>([]);

  const [type, setType] = React.useState<ITypes[]>([]);

  const [wire, setWire] = React.useState<IWire[]>([]);

  const [loadingSchedule, setLoadingSchedule] = React.useState<boolean>(false);

  const [alerts, setAlerts] = React.useState<any[]>([]);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [notifications, setNotifications] = React.useState<INotification[]>([]);

  const [responses, setResponses] = React.useState<
    { id: string; description: string; message: string }[]
  >([]);

  const [timeChoosen, setTimeChoosen] = React.useState<string>(() =>
    getCurrentLocalDateYMD()
  );

  const [timeSelect, setTimeSelect] = React.useState<string>(() =>
    formatDateLabelFromYMD(getCurrentLocalDateYMD())
  );

  const [timeRefresh, setTimeRefres] = React.useState<number>(36000);

  const [controlArrived, setControlArrived] = React.useState<
    "SCHEDULE" | "ARRIVED"
  >("ARRIVED");

  const [screensHome, setScreensHome] = React.useState<IPageHome>("DEFAULT");

  const [openCloseAniversario, setOpenCloseAniversario] =
    React.useState<boolean>(false);

  const [wireValues, setWireValues] = React.useState<ILabelValue[]>([]);

  const [typeValues, setTypeValues] = React.useState<ILabelValue[]>([]);

  const [orthodonticElasticValues, setOrthodonticElasticValues] = React.useState<ILabelValue[]>([]);

  const [showContactUpdate, setShowContactUpdate] = React.useState(false);
  const [contactUpdateUserId, setContactUpdateUserId] = React.useState("");
  const [contactUpdateDentistName, setContactUpdateDentistName] = React.useState("");

  const scheduleRequestRef = React.useRef<Set<string>>(new Set());

  const deslogged = React.useCallback(
    async (id: string) => {
      try {
        const { status, data } = await disconnect(id);

        if ([200, 201, 204].includes(status)) {
          localStorage.clear();
          navigate("/login");
        } else {
          toast.error(data.message);
        }
      } catch (error) {
        console.error("Erro ao desconectar:", error);
        toast.error("Ocorreu um erro ao tentar desconectar.");
      }
    },
    [navigate]
  );

  const transformScheduleData = React.useCallback(
    (scheduleData: Record<string, ScheduleLight[] | []>): TimeSlot[] => {
      const result: TimeSlot[] = [];

      Object.entries(scheduleData).forEach(([time, data]) => {
        /*data &&
          data.forEach((item) => {
            result.push({
              time,
              data: data ? [item] : [],
            });
          });*/

        if (data) {
          data.forEach((item) => {
            result.push({
              time,
              data: [item],
            });
          });
        } else {
          result.push({
            time,
            data: [],
          });
        }
      });

      return result;
    },
    []
  );

  const schedules = React.useCallback(
    async (values: ISchedule) => {
      if (!(scheduleRequestRef.current instanceof Set)) {
        scheduleRequestRef.current = new Set();
      }
      const requestKey = `${values.id}-${values.date}`;
      if (scheduleRequestRef.current.has(requestKey)) {
        return;
      }
      scheduleRequestRef.current.add(requestKey);
      setLoadingSchedule(true);

      try {
        const { status, data } = await getSchedule(values);

        if ([200, 201].includes(status)) {
          const transformedData = transformScheduleData(data.data);

          // Extrai os IDs dos novos dados
          const ids: number[] = [];

          transformedData.forEach((appointment) =>
            appointment.data.forEach((item) => {
              if (item.st_schedule === "C") {
                ids.push(item.id);
              }
            })
          );

          const lastAttendanceId = Number(
            localStorage.getItem("lastAttendanceId") || 0
          );

          const lastAttendanceRequestId = ids[ids.length - 1];

          if (lastAttendanceRequestId !== lastAttendanceId) {
            localStorage.setItem("lastAttendanceId", `${ids[ids.length - 1]}`);

            //@ts-ignore
            if (window && window.ReactNativeWebView) {
              // Chama a função nativa para tocar o áudio
              //@ts-ignore
              window.ReactNativeWebView.postMessage(
                JSON.stringify({
                  action: "playNotificationSound",
                  value: "",
                })
              );
            }
          }

          // Atualiza os dados no estado e no localStorage
          setScheduleData(transformedData);
        } else {
          toast.error(data.message);
          throw new Error(data.message);
        }
      } catch (e: any) {
        console.error(e);
      } finally {
        setLoadingSchedule(false);
        scheduleRequestRef.current.delete(requestKey);
      }
    },
    [transformScheduleData]
  );

  const materials = React.useCallback(
    async (values: { userId: string; idPac: number }) => {
      setLoading(true);

      try {
        setLoading(false);
        const { data: dataMaterial, status: statusMaterial } =
          await getMaterials(values.userId, values.idPac);

        if ([200, 201].includes(statusMaterial)) {
          const valuesComplementary: ILabelValue[] = [];
          const valuesTyp: ILabelValue[] = [];
          const valuesWire: ILabelValue[] = [];

          dataMaterial.data.complementary.forEach((item) => {
            valuesComplementary.push({
              label: item.name,
              value: item.id,
            });
          });

          dataMaterial.data.typ.forEach((item) => {
            valuesTyp.push({
              label: item.description,
              value: item.description,
            });
          });

          dataMaterial.data.wire.forEach((item) => {
            valuesWire.push({
              label: item.description,
              value: item.description,
            });
          });

          setTypeValues(valuesTyp);
          setWireValues(valuesWire);
          setComplementary(valuesComplementary);
          setType(dataMaterial.data.typ);
          setWire(dataMaterial.data.wire);
        }
      } catch (e: any) {
        setLoading(false);
      }
      setLoading(false);
    },
    []
  );

  const loadOrthodonticElastics = React.useCallback(
    async (userId: string) => {
      try {
        const { data: dataElastics, status: statusElastics } =
          await getOrthodonticElastics(userId);

        if ([200, 201].includes(statusElastics)) {
          const valuesElastics: ILabelValue[] = [];

          dataElastics.data.forEach((item) => {
            valuesElastics.push({
              label: item.description,
              value: item.id,
            });
          });

          setOrthodonticElasticValues(valuesElastics);
        }
      } catch (e: any) {
        console.error("Erro ao carregar elásticos ortodônticos:", e);
      }
    },
    []
  );

  const procedures = React.useCallback(
    async (values: { userId: string; type: number; idPac: number }) => {
      setLoading(true);

      try {
        setLoading(false);
        const { data: dataProcedures, status: statusProcedures } =
          await getProcedures(values.userId, values.type, values.idPac);

        if ([200, 201].includes(statusProcedures)) {
          const valuesComplementary: ILabelValueProcedure[] = [];

          dataProcedures.data.forEach((item) => {
            if (
              item.id !== 2 &&
              item.id !== 30 &&
              item.id !== 31 &&
              item.id !== 492
            ) {
              valuesComplementary.push({
                label: item.description,
                value: item.id,
                flow: item.flow,
              });
            }
          });

          setProceduresData(valuesComplementary);

          const valuesComplementaryWithFlow: ILabelValueProcedure[] = [];

          dataProcedures.data.forEach((item) => {
            if (item.flow !== 0) {
              valuesComplementaryWithFlow.push({
                label: item.description,
                value: item.id,
                flow: item.flow,
              });
            }
          });

          setProceduresDataWithFlow(valuesComplementaryWithFlow);
        }
      } catch (e: any) {
        setLoading(false);
      }
      setLoading(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const referrals = React.useCallback(
    async (values: { id: string; idSchedule: number; idPac: number }) => {
      setLoading(true);

      try {
        setLoading(false);
        const { data: dataProcedures, status: statusProcedures } =
          await getReferrals(values.id, values.idSchedule, values.idPac);

        if ([200, 201].includes(statusProcedures)) {
          const valuesComplementary: ILabelValue[] = [];

          dataProcedures.data.forEach((item) => {
            valuesComplementary.push({
              label: item.name,
              value: item.id,
            });
          });

          setReferralsData(valuesComplementary);
        }
      } catch (e: any) {
        setLoading(false);
      }
      setLoading(false);
    },
    []
  );

  const signIn = React.useCallback(
    async (values: IAuthLogin) => {
      setLoading(true);

      try {
        const { status, data: dataLogin } = await authLogin(values);

        if ([200, 201].includes(status)) {
          const authenticated: IAuthenticated = {
            auth: {
              message: dataLogin.message,
              success: dataLogin.success,
              data: {
                token: dataLogin.data.token,
                id: dataLogin.data.id,
                dentist: {
                  id: dataLogin.data.dentist.id,
                  name: dataLogin.data.dentist.name,
                  idUnit: dataLogin.data.dentist.idUnit,
                  appraiser: dataLogin.data.dentist.appraiser,
                  /*hour: {
                    startTimeMorning:
                      dataLogin.data.dentist.hour.startTimeMorning,
                    endTimeMorning: dataLogin.data.dentist.hour.endTimeMorning,
                    startTimeAfternoon:
                      dataLogin.data.dentist.hour.startTimeAfternoon,
                    endTimeAfternoon:
                      dataLogin.data.dentist.hour.endTimeAfternoon,
                  },*/
                },
              },
            },
          };

          localStorage.setItem(
            "userActual",
            JSON.stringify(authenticated.auth)
          );

          setAuthenticatedUser(authenticated);
          setLoading(false);
          toast.success(dataLogin.message);

          try {
            const { status: contatoStatus, data: contatoData } =
              await getDentistaContato(dataLogin.data.id);

            if (
              [200, 201].includes(contatoStatus) &&
              contatoData.success &&
              !contatoData.data
            ) {
              setContactUpdateUserId(dataLogin.data.id);
              setContactUpdateDentistName(dataLogin.data.dentist.name);
              setShowContactUpdate(true);
              return;
            }
          } catch {
            // Se falhar a verificação, segue para home normalmente
          }

          navigate("/home");
        } else {
          toast.error(dataLogin.message);
          throw new Error(dataLogin.message);
        }
      } catch (e: any) {
        setLoading(false);
      }
      setLoading(false);
    },
    [navigate]
  );

  const timeReset = React.useCallback(async () => {
    try {
      const { status, data } = await getTimeReset();

      if ([200, 201, 204].includes(status)) {
        const timeMiliseconds = data.data.times.refresh * 1000;

        setTimeRefres(timeMiliseconds);
      } else {
        toast.error(data.message);
      }
    } catch (e) {
      console.error(e);
    } finally {
    }
  }, []);

  function toastGlobal() {
    toast.success("Evolução carregada com sucesso!");
  }

  (window as any).toastGlobal = toastGlobal;

  React.useEffect(() => {
    timeReset();
  }, [timeReset]);

  const userActual = localStorage.getItem("userActual");

  const user = React.useMemo(() => {
    try {
      return userActual ? JSON.parse(userActual) : null;
    } catch {
      return null;
    }
  }, [userActual]);

  // Ref para acessar user nos callbacks sem causar re-render
  const userRef = React.useRef(user);
  React.useEffect(() => {
    userRef.current = user;
  }, [user]);

  const selectedDateRef = React.useRef(timeChoosen);
  React.useEffect(() => {
    selectedDateRef.current = timeChoosen;
  }, [timeChoosen]);

  // WebSocket para notificações em tempo real
  const dentistId = user?.data?.dentist?.id || 0;

  // Ref para controle de reload único
  const lastReloadRef = React.useRef<number>(0);

  // Função para recarregar schedules (máximo 1x a cada 5 segundos)
  const reloadSchedules = React.useCallback(() => {
    const now = Date.now();
    if (now - lastReloadRef.current < 5000) {
      return; // Muito recente
    }
    lastReloadRef.current = now;
    
    const currentUser = userRef.current;
    if (currentUser?.data?.id) {
      const selectedDate = selectedDateRef.current || getCurrentLocalDateYMD();
      console.log('[WS] Recarregando schedules...');
      getSchedule({ id: currentUser.data.id, date: selectedDate }).then(({ status, data }) => {
        if ([200, 201].includes(status)) {
          const transformedData = transformScheduleData(data.data);
          setScheduleData(transformedData);
        }
      });
    }
  }, [transformScheduleData]);

  const addNotification = React.useCallback((notification: INotification) => {
    setNotifications((prev) => {
      if (prev.some((n) => n.id === notification.id)) {
        return prev;
      }
      return [notification, ...prev];
    });
  }, []);

  const markNotificationRead = React.useCallback((id: number) => {
    const currentUser = userRef.current;
    if (!currentUser?.data?.id) {
      return;
    }

    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, is_lido: 1 } : n))
    );

    readyNotification(String(currentUser.data.id), id).catch((error) => {
      console.error("Erro ao marcar notificação como lida:", error);
    });
  }, []);

  // Carregar notificações existentes do backend
  const loadNotifications = React.useCallback(async (userId: string) => {
    try {
      const { status, data } = await getNotifications(userId);
      if ([200, 201].includes(status) && data?.data) {
        setNotifications(data.data);
      }
    } catch (e) {
      console.error("Erro ao carregar notificações:", e);
    }
  }, []);

  // Carregar notificações quando o usuário estiver logado
  React.useEffect(() => {
    if (user?.data?.id) {
      loadNotifications(user.data.id);
    }
  }, [user?.data?.id, loadNotifications]);
  
  // Callbacks estáveis - deduplicação já é feita no WebSocketManager
  const wsCallbacks = React.useMemo(() => ({
    onScheduleUpdated: (event: ScheduleUpdatedEvent) => {
      const selectedDate = selectedDateRef.current || getCurrentLocalDateYMD();
      if (event.date === selectedDate) {
        reloadSchedules();
      }
    },
    onPatientArrived: (event: PatientArrivedEvent) => {
      // Ignorar se estamos atendendo esse mesmo paciente (evita notificação falsa durante o atendimento)
      try {
        const personAttendance = localStorage.getItem("personAttendance");
        if (personAttendance) {
          const current = JSON.parse(personAttendance);
          if (
            current?.pac_id === event.patient_id ||
            current?.id === event.schedule_id
          ) {
            console.log('[WS] Ignorando patient.arrived - mesmo paciente em atendimento');
            return;
          }
        }
      } catch {
        // ignorar erro de parse
      }
      console.log('[WS] Paciente chegou:', event.patient_name);
      // @ts-ignore
      if (window.ReactNativeWebView) {
        // @ts-ignore
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ action: 'playNotificationSound', value: '' })
        );
      }
      toast.success(`Paciente ${event.patient_name} chegou!`, { duration: 5000 });
      reloadSchedules();
    },
    onDentistNotification: (event: DentistNotificationEvent) => {
      const currentUser = userRef.current;
      addNotification({
        id: event.notification_id,
        unidade_id: event.unit_id,
        dentista_id: event.dentist_id,
        usuario_id: currentUser?.data?.id || 0,
        data_cadastro: event.created_at,
        titulo: event.title,
        mensagem: event.message,
        is_enviado: "1",
        data_envio: event.created_at,
        is_lido: 0,
      });
      toast.success(`Nova notificação: ${event.title}`, { duration: 5000 });
    },
    onConnected: () => console.log('[WS] Conectado'),
    onDisconnected: () => console.log('[WS] Desconectado'),
    onError: () => {},
  }), [reloadSchedules, addNotification]);

  const { isConnected: isWebSocketConnected } = useWebSocket(
    {
      ...WEBSOCKET_CONFIG,
      dentistId,
      enabled: WEBSOCKET_CONFIG.enabled && dentistId > 0,
    },
    wsCallbacks
  );

  const value = React.useMemo(
    () => ({
      signIn,
      loading,
      freeTime,
      setFreeTime,
      deslogged,
      searchPatients,
      setSearchPatients,
      schedules,
      user,
      scheduleData,
      complementary,
      type,
      wire,
      proceduresData,
      loadingSchedule,
      procedures,
      materials,
      referralsData,
      referrals,
      alerts,
      setAlerts,
      modalOpen,
      setModalOpen,
      responses,
      setResponses,
      timeChoosen,
      setTimeChoosen,
      timeSelect,
      setTimeSelect,
      showServed,
      setShowServed,
      controlArrived,
      setControlArrived,
      timeRefresh,
      proceduresDataWithFlow,
      screensHome,
      setScreensHome,
      openCloseAniversario,
      setOpenCloseAniversario,
      wireValues,
      typeValues,
      orthodonticElasticValues,
      loadOrthodonticElastics,
      isWebSocketConnected,
      notifications,
      markNotificationRead,
      showContactUpdate,
    }),
    [
      signIn,
      loading,
      freeTime,
      setFreeTime,
      deslogged,
      searchPatients,
      setSearchPatients,
      schedules,
      user,
      scheduleData,
      complementary,
      type,
      wire,
      proceduresData,
      loadingSchedule,
      procedures,
      materials,
      referralsData,
      referrals,
      alerts,
      setAlerts,
      modalOpen,
      setModalOpen,
      responses,
      setResponses,
      timeChoosen,
      setTimeChoosen,
      timeSelect,
      setTimeSelect,
      showServed,
      setShowServed,
      controlArrived,
      setControlArrived,
      timeRefresh,
      proceduresDataWithFlow,
      screensHome,
      setScreensHome,
      openCloseAniversario,
      setOpenCloseAniversario,
      orthodonticElasticValues,
      loadOrthodonticElastics,
      isWebSocketConnected,
      notifications,
      markNotificationRead,
      showContactUpdate,
      typeValues,
      wireValues,
    ]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      <ContactUpdateModal
        open={showContactUpdate}
        userId={contactUpdateUserId}
        dentistName={contactUpdateDentistName}
        onSaved={() => {
          setShowContactUpdate(false);
          navigate("/home");
        }}
      />
    </AuthContext.Provider>
  );
};

const useAuth = (): IAuthContext => React.useContext(AuthContext);

export { AuthContext, AuthProvider, useAuth };
